/**
 * Trình dựng luồng v2 (React Flow, kế thừa cách làm của automation-flow-page cũ).
 * Khối: Nguồn · Ánh xạ · Điều kiện · Chờ · Hành động. Bảng phải cấu hình khối
 * đang chọn; ô văn bản chèn biến {{…}} bằng bộ chọn trường theo schema CRM.
 * "Chạy thử" chạy runner trên trình duyệt với sự kiện mẫu, tô màu từng khối.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  ReactFlow, ReactFlowProvider, Background, Controls, MiniMap, addEdge,
  useNodesState, useEdgesState, useReactFlow, useNodesInitialized, getNodesBounds, MarkerType,
  type Node, type Edge, type Connection, type NodeTypes,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import { toast } from 'sonner'
import {
  ArrowLeft, Save, Play, Plus, Trash2, Plug, Braces, X, Database, CalendarClock, MousePointerClick, LayoutGrid, History, Sparkles, Zap,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea, Switch } from '@/components/ui/misc'
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Loading } from '@/components/shared/feedback'
import { cn } from '@/lib/utils'
import { storeV2, useStoreV2 } from '@/hooks/use-automation-v2'
import {
  ACTIONS_V2, ACTION_V2_LABELS, CONDITION_OPS_V2, CONDITION_OP_V2_LABELS, SOURCE_EVENTS, SOURCE_LABELS, genId,
  type ConditionRuleV2, type FlowNodeV2, type NodeKind, type WorkflowRun, type WorkflowV2, type SampleEvent,
} from '@/lib/automation-v2/types'
import { CRM_SCHEMA, GSHEET_SAMPLES, KIOTVIET_SAMPLES, type SchemaField } from '@/lib/automation-v2/kiotviet'
import {
  INTERNAL_OBJECTS, INTERNAL_EVENT_LABELS, INTERNAL_SAMPLES, SCHEDULE_SAMPLES, TRIGGER_MODE_LABELS,
  type TriggerMode, type InternalObject, type InternalEvent,
} from '@/lib/automation-v2/kich-hoat'
import { chayThu } from '@/lib/automation-v2/runner'
import { ChiTietLanChay, BieuTuongBuoc } from './chi-tiet-lan-chay'
import { KhoiCanvas, KHOI_META, CanvasCtx, ChonKichHoatDialog, ChonKhoiDialog, ChonMauDialog, HANH_DONG_META, type BlockData } from './khoi-canvas'
import { NhatKyDialog } from './nhat-ky-hoat-dong'
import { ThanhAiLuong, type LuongCanvas } from './thanh-ai-luong'
import { ChayThatDialog, type BuocChay } from './chay-that-dialog'
import { ChonMauZns } from './chon-mau-zns'
import { luongTuMau, type MauWorkflow } from '@/lib/automation-v2/mau-workflow'
import { suyRaSuKien } from '@/lib/automation-v2/suy-ra-su-kien'

// ── Node trên canvas: xem khoi-canvas.tsx ────────────────────────────
type BlockNode = Node<BlockData>
const KHOI = KHOI_META
const nodeTypes: NodeTypes = { source: KhoiCanvas, mapping: KhoiCanvas, condition: KhoiCanvas, delay: KhoiCanvas, action: KhoiCanvas }

// ── Chuyển đổi ───────────────────────────────────────────────────────
function toRF(wf: WorkflowV2): { nodes: BlockNode[]; edges: Edge[] } {
  return {
    nodes: wf.nodes.map((n) => ({ id: n.id, type: n.kind, position: n.position, data: { kind: n.kind, label: n.label, config: n.config } })),
    edges: wf.edges.map((e, i) => ({
      id: `e_${e.source}_${e.target}_${i}`, source: e.source, target: e.target, sourceHandle: e.label,
      type: 'smoothstep', markerEnd: { type: MarkerType.ArrowClosed },
    })),
  }
}
function fromRF(wf: WorkflowV2, nodes: BlockNode[], edges: Edge[]): WorkflowV2 {
  return {
    ...wf,
    nodes: nodes.map((n) => ({ id: n.id, kind: n.data.kind, label: n.data.label, position: n.position, config: n.data.config })),
    edges: edges.map((e) => ({ source: e.source, target: e.target, ...(e.sourceHandle === 'true' || e.sourceHandle === 'false' ? { label: e.sourceHandle } : {}) })),
  }
}

// ── Trang ────────────────────────────────────────────────────────────
export function FlowV2Page() {
  return <ReactFlowProvider><Editor /></ReactFlowProvider>
}

function Editor() {
  const { id } = useParams<{ id: string }>()
  const nav = useNavigate()
  const [params, setParams] = useSearchParams()
  const { workflows, mappings, connectors } = useStoreV2()
  const wf = workflows.find((w) => w.id === id || w.templateKey === id)
  const [nodes, setNodes, onNodesChange] = useNodesState<BlockNode>([])
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([])
  const rf = useReactFlow()
  const khungRef = useRef<HTMLDivElement>(null)
  const daDoKhoi = useNodesInitialized()
  const [canFit, setCanFit] = useState(false)
  const [ten, setTen] = useState('')
  const [selId, setSelId] = useState<string | null>(null)
  const [run, setRun] = useState<WorkflowRun | null>(null)
  const [moRun, setMoRun] = useState(false)
  // Mở thẳng Nhật ký khi vào từ danh sách (/flow/:id?nhatky=1).
  const [moNhatKy, setMoNhatKy] = useState(params.get('nhatky') === '1')
  const [moMau, setMoMau] = useState(false)
  const [moChayThat, setMoChayThat] = useState(false)
  const [daNap, setDaNap] = useState<string | null>(null)
  /** Hộp chọn khối tiếp theo: khối cha + nhánh (với Điều kiện). */
  const [chonKhoi, setChonKhoi] = useState<{ sau: string | null; handle?: 'true' | 'false' } | null>(null)
  const [chonKichHoat, setChonKichHoat] = useState(false)
  const [nhomKichHoat, setNhomKichHoat] = useState<TriggerMode | undefined>(undefined)

  useEffect(() => {
    if (!wf || daNap === wf.id) return
    const g = toRF(wf); setNodes(g.nodes); setEdges(g.edges); setTen(wf.name); setDaNap(wf.id); setCanFit(true)
  }, [wf, daNap, setNodes, setEdges])
  // Khối được nạp sau khi mount nên fitView của ReactFlow chạy sớm quá → gọi lại khi đã đo xong kích thước khối.
  useEffect(() => {
    if (!canFit || !daDoKhoi) return
    setCanFit(false)
    const el = khungRef.current; const b = getNodesBounds(rf.getNodes())
    if (!el || !b.width) { rf.fitView({ padding: 0.15, maxZoom: 1 }); return }
    const w = el.clientWidth, h = el.clientHeight
    // Không thu nhỏ quá 0,55 — nhỏ hơn thì chữ trong khối không đọc được; thay vào đó canh mép trái để thấy khối Kích hoạt.
    const zoom = Math.min(1, Math.max(0.55, Math.min((w - 80) / b.width, (h - 80) / b.height)))
    const vua = b.width * zoom <= w - 80
    rf.setViewport({ zoom, x: vua ? (w - b.width * zoom) / 2 - b.x * zoom : 40 - b.x * zoom, y: Math.max(24, (h - b.height * zoom) / 2) - b.y * zoom })
  }, [canFit, daDoKhoi, rf])

  const onConnect = useCallback((c: Connection) => setEdges((eds) => addEdge({ ...c, type: 'smoothstep', markerEnd: { type: MarkerType.ArrowClosed } }, eds)), [setEdges])


  // Esc: bỏ chọn khối → ẩn bảng cấu hình (bỏ qua khi đang gõ trong ô nhập)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || !selId) return
      const t = e.target as HTMLElement | null
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return
      setSelId(null); setNodes((ns) => ns.map((n) => (n.selected ? { ...n, selected: false } : n)))
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [selId, setNodes])

  if (!wf) return <div className="p-6"><Loading label="Đang tải luồng..." /><Button variant="link" onClick={() => nav('/automation-v2')}>Về danh sách</Button></div>

  const sel = nodes.find((n) => n.id === selId) ?? null
  const capNhatSel = (patch: Partial<BlockData>) => setNodes((ns) => ns.map((n) => (n.id === selId ? { ...n, data: { ...n.data, ...patch } } : n)))
  const capNhatCfg = (patch: Record<string, unknown>) => sel && capNhatSel({ config: { ...sel.data.config, ...patch } })

  /** Thêm khối; nếu có khối cha thì đặt bên phải và nối dây (bố cục trái → phải). */
  const them = (kind: NodeKind, cfgSan?: Record<string, unknown>, sau?: { id: string | null; handle?: 'true' | 'false' }) => {
    const cha = sau === undefined ? sel : sau.id ? nodes.find((n) => n.id === sau.id) ?? null : null
    const handle = sau === undefined ? (sel?.data.kind === 'condition' ? 'true' : undefined) : sau.handle
    const base = cha?.position ?? nodes[nodes.length - 1]?.position ?? { x: 40, y: 40 }
    let pos = { x: base.x + 360, y: base.y + (handle === 'false' ? 320 : 0) }
    while (nodes.some((n) => Math.abs(n.position.x - pos.x) < 200 && Math.abs(n.position.y - pos.y) < 200)) pos = { x: pos.x, y: pos.y + 320 }
    const nid = genId('n')
    const cfg: Record<string, unknown> = cfgSan ?? (kind === 'source' ? { mode: 'internal', object: 'customer', internalEvent: 'created' } : kind === 'mapping' ? { mappingId: mappings[0]?.id ?? '' } : kind === 'condition' ? { logic: 'and', rules: [{ field: 'order.items[].category', op: 'eq', value: '' }] } : kind === 'delay' ? { value: 24, unit: 'hours' } : { type: 'add_tag', tag: '' })
    const label = kind === 'action' ? ACTION_V2_LABELS[String(cfg.type) as keyof typeof ACTION_V2_LABELS] ?? KHOI[kind].nhan : KHOI[kind].nhan
    setNodes((ns) => [...ns.map((n) => ({ ...n, selected: false })), { id: nid, type: kind, position: pos, selected: true, data: { kind, label, config: cfg } }])
    if (cha && kind !== 'source') setEdges((es) => [...es, { id: genId('e'), source: cha.id, target: nid, sourceHandle: handle, type: 'smoothstep', markerEnd: { type: MarkerType.ArrowClosed } }])
    setSelId(nid)
  }
  /** Bỏ chọn khối → ẩn bảng cấu hình. */
  const boChon = () => { setSelId(null); setNodes((ns) => ns.map((n) => (n.selected ? { ...n, selected: false } : n))) }
  const xoaSel = () => { if (!sel || sel.data.kind === 'source') return; setNodes((ns) => ns.filter((n) => n.id !== sel.id)); setEdges((es) => es.filter((e) => e.source !== sel.id && e.target !== sel.id)); setSelId(null) }

  /** Lưu và bật luồng — bật công tắc để luồng bắt đầu chạy thật. */
  /** Lưu và bật luồng — bật là máy chủ bắt đầu chạy thật khi có sự kiện khớp. */
  const luuVaChay = async () => {
    const kq = await storeV2.saveWorkflow({ ...fromRF(wf, nodes, edges), name: ten || wf.name, enabled: true })
    if (kq) toast.success('Đã lưu và bật luồng — máy chủ sẽ chạy khi có sự kiện khớp')
  }
  const luu = async () => {
    const kq = await storeV2.saveWorkflow({ ...fromRF(wf, nodes, edges), name: ten || wf.name })
    if (kq) toast.success(kq.enabled ? 'Đã lưu luồng (đang bật — áp dụng ngay cho sự kiện mới)' : 'Đã lưu (luồng đang tắt)')
  }

  /** Chạy thử chính luồng đang ở canvas: dữ liệu thử suy ra từ khối Kích hoạt. */
  const chay = () => {
    const wfHienTai = fromRF(wf, nodes, edges)
    const nguon = wfHienTai.nodes.find((n) => n.kind === 'source')
    if (!nguon) { toast.error('Luồng chưa có khối Kích hoạt'); return }
    const sample = suyRaSuKien(nguon.config, connectors)
    const kq = chayThu(wfHienTai, sample, mappings)
    setRun(kq); storeV2.addRun(kq); setMoRun(true)
    const theoNode = new Map(kq.steps.map((s) => [s.nodeId, s]))
    setNodes((ns) => ns.map((n) => { const s = theoNode.get(n.id); return { ...n, data: { ...n.data, runStatus: s ? s.status : 'skip', branch: s?.branch } } }))
    toast[kq.status === 'ok' ? 'success' : 'error'](kq.status === 'ok' ? `Chạy thử xong: ${kq.steps.length} bước — đã ghi vào Nhật ký` : 'Chạy thử dừng vì lỗi — xem Nhật ký')
  }

  /** Luồng đang ở canvas (gửi cho AI) và áp luồng AI trả về lên canvas. */
  const layLuongCanvas = (): LuongCanvas => { const w = fromRF(wf, nodes, edges); return { name: ten || wf.name, nodes: w.nodes, edges: w.edges } }
  const apLuongCanvas = (l: LuongCanvas) => {
    const g = toRF({ ...wf, name: l.name || ten || wf.name, nodes: l.nodes, edges: l.edges })
    setNodes(g.nodes); setEdges(g.edges); setSelId(null); setRun(null); setCanFit(true)
    if (l.name) setTen(l.name)
  }

  /** Áp một mẫu trong thư viện lên canvas (giữ id luồng đang mở). */
  const apMau = (mau: MauWorkflow) => {
    const moi = luongTuMau(mau, wf)
    const g = toRF(moi); setNodes(g.nodes); setEdges(g.edges); setTen(moi.name); setSelId(null); setRun(null); setCanFit(true)
    toast.success(`Đã áp mẫu "${mau.ten}" — sửa nội dung rồi bấm Lưu và chạy`)
  }
  const xoaVet = () => setNodes((ns) => ns.map((n) => ({ ...n, data: { ...n.data, runStatus: undefined, branch: undefined } })))

  return (
    <div className="-m-6 flex h-[calc(100vh-4rem)] flex-col">
      <div className="flex flex-wrap items-center gap-2 border-b bg-background px-4 py-2">
        <Button variant="ghost" size="sm" onClick={() => nav('/automation-v2')}><ArrowLeft className="h-4 w-4" /></Button>
        <Input value={ten} onChange={(e) => setTen(e.target.value)} className="h-8 w-72 font-semibold" />
        <div className="flex items-center gap-1.5 rounded-md border px-2 py-1" title="Bật thì luồng chạy thật khi có sự kiện">
          <Switch checked={wf.enabled} onCheckedChange={(v) => { storeV2.toggleWorkflow(wf.id, v); toast.success(v ? 'Đã bật luồng' : 'Đã tắt luồng') }} />
          <span className={cn('text-[11px] font-medium', wf.enabled ? 'text-success' : 'text-muted-foreground')}>{wf.enabled ? 'Đang bật' : 'Đang tắt'}</span>
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => setMoMau(true)}><Sparkles className="h-3.5 w-3.5" /> Mẫu có sẵn</Button>
          <Button size="sm" variant="outline" onClick={chay}><Play className="h-3.5 w-3.5" /> Chạy thử</Button>
          <Button size="sm" variant="outline" className="border-warning/60 text-warning hover:text-warning" onClick={() => setMoChayThat(true)} title="Chạy luồng với dữ liệu thật — gửi tin Zalo cho khách"><Zap className="h-3.5 w-3.5" /> Chạy thật</Button>
          <Button size="sm" variant="ghost" onClick={() => setMoNhatKy(true)}><History className="h-3.5 w-3.5" /> Nhật ký</Button>
          {run && <Button size="sm" variant="ghost" onClick={xoaVet} title="Xoá màu kết quả trên canvas"><X className="h-3.5 w-3.5" /></Button>}
          <Button size="sm" variant="outline" onClick={luu}><Save className="h-3.5 w-3.5" /> Lưu</Button>
          <Button size="sm" onClick={luuVaChay}><Save className="h-3.5 w-3.5" /> Lưu và chạy</Button>
        </div>
      </div>

      <div className="flex min-h-0 flex-1">
        {/* Bảng khối */}
        <aside className="w-44 shrink-0 space-y-1 overflow-y-auto border-r bg-muted/20 p-2">
          <p className="px-1 text-[10.5px] font-bold uppercase tracking-wide text-muted-foreground">Kích hoạt</p>
          {(Object.keys(TRIGGER_MODE_LABELS) as TriggerMode[]).map((m) => { const I = m === 'internal' ? Database : m === 'external' ? Plug : m === 'schedule' ? CalendarClock : MousePointerClick; const src = nodes.find((n) => n.data.kind === 'source'); const active = String(src?.data.config.mode ?? 'external') === m; return (
            <button key={m} type="button" title={`${TRIGGER_MODE_LABELS[m].moTa} — bấm để chọn kích hoạt loại này`} onClick={() => { if (!src) return; setSelId(src.id); setNhomKichHoat(m); setChonKichHoat(true) }}
              className={cn('flex w-full items-center gap-2 rounded-md border px-2 py-1.5 text-left text-[11px] hover:bg-accent/60', active ? 'border-primary bg-primary/10 text-primary' : 'bg-card')}>
              <I className="h-3.5 w-3.5 shrink-0" /> <span className="leading-tight">{TRIGGER_MODE_LABELS[m].ten}</span>
            </button>
          ) })}
          <p className="px-1 pt-2 text-[10.5px] font-bold uppercase tracking-wide text-muted-foreground">Thêm khối</p>
          <button type="button" onClick={() => setChonKhoi({ sau: sel?.id ?? null, handle: sel?.data.kind === 'condition' ? 'true' : undefined })} className="flex w-full items-center gap-2 rounded-md border border-primary/40 bg-primary/10 px-2 py-1.5 text-left text-xs font-medium text-primary hover:bg-primary/15" title={sel ? `Nối sau "${sel.data.label}"` : 'Thêm vào canvas'}>
            <LayoutGrid className="h-3.5 w-3.5" /> Chọn từ thư viện <Plus className="ml-auto h-3 w-3" />
          </button>
          {(['condition', 'delay', 'mapping'] as NodeKind[]).map((k) => { const K = KHOI[k]; const I = K.icon; return (
            <button key={k} type="button" onClick={() => them(k)} className="flex w-full items-center gap-2 rounded-md border bg-card px-2 py-1.5 text-left text-xs hover:bg-accent/60" title={sel ? `Nối sau "${sel.data.label}"` : 'Thêm vào canvas'}>
              <I className="h-3.5 w-3.5 text-muted-foreground" /> {K.nhan} <Plus className="ml-auto h-3 w-3 text-muted-foreground" />
            </button>
          ) })}
          <p className="px-1 pt-1 text-[10.5px] font-bold uppercase tracking-wide text-muted-foreground">Hành động nhanh</p>
          {(['send_zalo_personal', 'send_zalo_oa', 'add_tag', 'create_task', 'create_voucher'] as const).map((a) => { const M = HANH_DONG_META[a]; const I = M.icon; return (
            <button key={a} type="button" onClick={() => them('action', { type: a, ...(a === 'create_voucher' ? { prefix: 'MVS', value: 10, kind: 'percent', expireDays: 30 } : a === 'create_task' ? { dueHours: 24 } : {}) })} className="flex w-full items-center gap-2 rounded-md border bg-card px-2 py-1.5 text-left text-xs hover:bg-accent/60">
              <I className="h-3.5 w-3.5 text-muted-foreground" /> <span className="truncate">{ACTION_V2_LABELS[a]}</span> <Plus className="ml-auto h-3 w-3 shrink-0 text-muted-foreground" />
            </button>
          ) })}
          <p className="px-1 pt-2 text-[10.5px] text-muted-foreground">Bấm "Bước tiếp theo ⊕" trên khối để thêm khối nối sau; kéo chấm tròn để nối dây. Điều kiện có 2 đầu ra Đúng / Sai.</p>
        </aside>

        <div ref={khungRef} className="relative min-w-0 flex-1">
          <CanvasCtx.Provider value={{
            themSau: (nid, handle) => setChonKhoi({ sau: nid, handle }),
            doiKichHoat: (nid) => { setSelId(nid); setNhomKichHoat(undefined); setChonKichHoat(true) },
            enabled: wf.enabled,
            toggle: (v) => { storeV2.toggleWorkflow(wf.id, v); toast.success(v ? 'Đã bật luồng' : 'Đã tắt luồng') },
          }}>
            <ReactFlow
              nodes={nodes} edges={edges} nodeTypes={nodeTypes}
              onNodesChange={onNodesChange} onEdgesChange={onEdgesChange} onConnect={onConnect}
              onSelectionChange={({ nodes: ns }) => setSelId(ns[0]?.id ?? null)}
              defaultEdgeOptions={{ type: 'smoothstep', markerEnd: { type: MarkerType.ArrowClosed } }}
              fitViewOptions={{ padding: 0.15, maxZoom: 1 }} minZoom={0.2} deleteKeyCode={['Backspace', 'Delete']}
            >
              <Background gap={16} /><Controls /><MiniMap pannable zoomable nodeStrokeWidth={2} className="!bottom-20" />
            </ReactFlow>
          </CanvasCtx.Provider>
          <ThanhAiLuong layLuong={layLuongCanvas} apLuong={apLuongCanvas} />
        </div>

        {/* Bảng cấu hình — chỉ hiện khi đang chọn một khối, để canvas dùng hết bề ngang */}
        {sel && (
        <aside className="w-[22rem] shrink-0 overflow-y-auto border-l bg-background p-3">
          {(
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Badge variant="outline">{KHOI[sel.data.kind].nhan}</Badge>
                <div className="ml-auto flex items-center gap-1">
                  {sel.data.kind !== 'source' && <Button size="sm" variant="ghost" className="text-destructive" onClick={xoaSel} title="Xoá khối"><Trash2 className="h-3.5 w-3.5" /></Button>}
                  <Button size="sm" variant="ghost" onClick={boChon} title="Đóng bảng cấu hình (Esc)"><X className="h-4 w-4" /></Button>
                </div>
              </div>
              <div><Label className="text-xs">Tên khối</Label><Input value={sel.data.label} onChange={(e) => capNhatSel({ label: e.target.value })} className="h-8" /></div>
              {sel.data.kind === 'source' && <CauHinhNguon cfg={sel.data.config} set={capNhatCfg} />}
              {sel.data.kind === 'mapping' && <CauHinhAnhXa cfg={sel.data.config} set={capNhatCfg} />}
              {sel.data.kind === 'condition' && <CauHinhDieuKien cfg={sel.data.config} set={capNhatCfg} />}
              {sel.data.kind === 'delay' && <CauHinhCho cfg={sel.data.config} set={capNhatCfg} />}
              {sel.data.kind === 'action' && <CauHinhHanhDong cfg={sel.data.config} set={capNhatCfg} />}
              {run && (() => { const s = run.steps.find((x) => x.nodeId === sel.id); return s ? (
                <div className="rounded-md border bg-muted/30 p-2 text-[11px]"><p className="mb-1 flex items-center gap-1 font-medium"><BieuTuongBuoc status={s.status} className="h-3.5 w-3.5" /> Lần chạy thử gần nhất</p><p className="whitespace-pre-wrap text-muted-foreground">{s.summary}</p>{s.output !== undefined && <pre className="mt-1 max-h-40 overflow-auto rounded bg-background p-1.5">{JSON.stringify(s.output, null, 2)}</pre>}</div>
              ) : null })()}
            </div>
          )}
        </aside>
        )}
      </div>

      <Dialog open={moRun} onOpenChange={setMoRun}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle className="flex items-center gap-2">Vết chạy thử {run && <Badge variant={run.status === 'ok' ? 'success' : 'destructive'}>{run.status === 'ok' ? 'Thành công' : 'Lỗi'}</Badge>}</DialogTitle><DialogDescription>{run?.sampleLabel}. Bấm một bước để xem đầu vào / đầu ra; các khối Chờ được tua nhanh.</DialogDescription></DialogHeader>
          {run && <ChiTietLanChay run={run} onChonKhoi={(nid) => setSelId(nid)} />}
        </DialogContent>
      </Dialog>
      <NhatKyDialog open={moNhatKy} onClose={() => { setMoNhatKy(false); if (params.get('nhatky')) setParams({}, { replace: true }) }} workflowId={wf.id} tenLuong={ten || wf.name} />
      <ChayThatDialog
        open={moChayThat} onClose={() => setMoChayThat(false)} tenLuong={ten || wf.name} layLuong={layLuongCanvas}
        onBuoc={(b: BuocChay) => setNodes((ns) => ns.map((n) => (n.id === b.nodeId ? { ...n, data: { ...n.data, runStatus: b.trangThai } } : n)))}
        onXong={() => storeV2.saveWorkflow({ ...fromRF(wf, nodes, edges), name: ten || wf.name })}
      />
      <ChonMauDialog open={moMau} onClose={() => setMoMau(false)} coKhoi={nodes.length} onChon={apMau} />
      <ChonKhoiDialog open={!!chonKhoi} onClose={() => setChonKhoi(null)} onChon={(kind, cfg) => them(kind, cfg, { id: chonKhoi?.sau ?? null, handle: chonKhoi?.handle })} />
      <ChonKichHoatDialog open={chonKichHoat} nhomMacDinh={nhomKichHoat} onClose={() => setChonKichHoat(false)} onChon={(patch) => { const src = nodes.find((n) => n.data.kind === 'source'); if (!src) return; setNodes((ns) => ns.map((n) => (n.id === src.id ? { ...n, data: { ...n.data, config: { filterRules: (n.data.config.filterRules as unknown[]) ?? [], ...patch } } } : n))); setSelId(src.id); toast.success('Đã đổi loại kích hoạt — chỉnh chi tiết ở bảng bên phải') }} />
    </div>
  )
}

// ── Bảng cấu hình từng loại ──────────────────────────────────────────
type CfgProps = { cfg: Record<string, unknown>; set: (p: Record<string, unknown>) => void }

function CauHinhNguon({ cfg, set }: CfgProps) {
  const { connectors } = useStoreV2()
  const mode = String(cfg.mode ?? 'external') as TriggerMode
  const c = connectors.find((x) => x.id === cfg.connectorId)
  const obj = INTERNAL_OBJECTS[cfg.object as InternalObject]
  const truongObj = (obj?.truong ?? []).flatMap((f) => (f.children ? f.children : [f])).filter((f) => f.type !== 'array' && f.type !== 'object')
  const filterRules = (cfg.filterRules as ConditionRuleV2[]) ?? []
  const suaLoc = (i: number, p: Partial<ConditionRuleV2>) => set({ filterRules: filterRules.map((r, k) => (k === i ? { ...r, ...p } : r)) })
  return (
    <>
      <div><Label className="text-xs">Kích hoạt khi</Label>
        <Select value={mode} onValueChange={(v) => set({ mode: v })}>
          <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>{(Object.keys(TRIGGER_MODE_LABELS) as TriggerMode[]).map((m) => <SelectItem key={m} value={m}>{TRIGGER_MODE_LABELS[m].ten}</SelectItem>)}</SelectContent>
        </Select>
        <p className="mt-1 text-[11px] text-muted-foreground">{TRIGGER_MODE_LABELS[mode].moTa}</p>
      </div>

      {mode === 'internal' && (
        <>
          <div><Label className="text-xs">Đối tượng</Label>
            <Select value={String(cfg.object ?? '')} onValueChange={(v) => set({ object: v, field: '', toValue: '' })}>
              <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Chọn đối tượng" /></SelectTrigger>
              <SelectContent>{(Object.keys(INTERNAL_OBJECTS) as InternalObject[]).map((k) => <SelectItem key={k} value={k}>{INTERNAL_OBJECTS[k].ten}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Sự kiện</Label>
            <Select value={String(cfg.internalEvent ?? '')} onValueChange={(v) => set({ internalEvent: v })}>
              <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Chọn sự kiện" /></SelectTrigger>
              <SelectContent>{(Object.keys(INTERNAL_EVENT_LABELS) as InternalEvent[]).map((k) => <SelectItem key={k} value={k}>{INTERNAL_EVENT_LABELS[k].ten}</SelectItem>)}</SelectContent>
            </Select>
            {!!cfg.internalEvent && <p className="mt-1 text-[11px] text-muted-foreground">{INTERNAL_EVENT_LABELS[cfg.internalEvent as InternalEvent]?.moTa}</p>}
          </div>
          {cfg.internalEvent === 'field_changed' && (
            <div className="space-y-1 rounded-md border p-2">
              <Label className="text-xs">Trường theo dõi</Label>
              <Select value={String(cfg.field ?? '')} onValueChange={(v) => set({ field: v })}>
                <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Chọn trường" /></SelectTrigger>
                <SelectContent>{truongObj.map((f) => <SelectItem key={f.path} value={f.path}><span className="font-mono">{f.path.split('.').pop()}</span> <span className="text-muted-foreground">— {f.label}</span></SelectItem>)}</SelectContent>
              </Select>
              <Label className="text-xs">Chỉ khi đổi thành (để trống = mọi thay đổi)</Label>
              <Input value={String(cfg.toValue ?? '')} onChange={(e) => set({ toValue: e.target.value })} className="h-8 text-xs" placeholder="vd: Vàng · Hoàn thành · vip" />
            </div>
          )}
          {(cfg.internalEvent === 'tag_added' || cfg.internalEvent === 'tag_removed') && (
            <p className="text-[11px] text-muted-foreground">Nhãn vừa gắn/gỡ nằm ở trường <code>{obj?.goc ?? 'x'}.tag_moi</code> — lọc bằng "Chỉ khi" bên dưới.</p>
          )}
        </>
      )}

      {mode === 'external' && (
        <>
          <div><Label className="text-xs">Nguồn dữ liệu</Label>
            <Select value={String(cfg.connectorId ?? '')} onValueChange={(v) => set({ connectorId: v, event: '' })}>
              <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Chọn nguồn" /></SelectTrigger>
              <SelectContent>{connectors.map((x) => <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Sự kiện từ nguồn</Label>
            <Select value={String(cfg.event ?? '')} onValueChange={(v) => set({ event: v })}>
              <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Chọn sự kiện" /></SelectTrigger>
              <SelectContent>{(c ? SOURCE_EVENTS[c.type] : []).map((e) => <SelectItem key={e.key} value={e.key}>{e.label} — <span className="text-muted-foreground">{e.key}</span></SelectItem>)}</SelectContent>
            </Select>
            {c && <p className="mt-1 text-[11px] text-muted-foreground">{SOURCE_EVENTS[c.type].find((e) => e.key === cfg.event)?.moTa} Dữ liệu ngoài cần khối <b>Ánh xạ</b> ngay sau để đổi sang trường CRM.</p>}
          </div>
        </>
      )}

      {mode === 'schedule' && (
        <>
          <div><Label className="text-xs">Lịch chạy</Label>
            <Select value={String(cfg.cron ?? '0 9 * * *')} onValueChange={(v) => set({ cron: v })}>
              <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="0 9 * * *">Hằng ngày 09:00</SelectItem>
                <SelectItem value="0 18 * * *">Hằng ngày 18:00</SelectItem>
                <SelectItem value="0 9 * * 1">Thứ Hai 09:00</SelectItem>
                <SelectItem value="0 8 1 * *">Ngày 1 hằng tháng 08:00</SelectItem>
                <SelectItem value="*/30 * * * *">Mỗi 30 phút</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Quét đối tượng</Label>
            <Select value={String(cfg.object ?? 'customer')} onValueChange={(v) => set({ object: v })}>
              <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>{(Object.keys(INTERNAL_OBJECTS) as InternalObject[]).map((k) => <SelectItem key={k} value={k}>{INTERNAL_OBJECTS[k].ten}</SelectItem>)}</SelectContent>
            </Select>
            <p className="mt-1 text-[11px] text-muted-foreground">Mỗi lần chạy, luồng chạy cho TỪNG bản ghi thoả lọc "Chỉ khi" (vd sinh nhật hôm nay, chưa trả lời &gt; 24h).</p>
          </div>
        </>
      )}

      {mode === 'manual' && (
        <>
          <div><Label className="text-xs">Nhãn nút</Label><Input value={String(cfg.buttonLabel ?? '')} onChange={(e) => set({ buttonLabel: e.target.value })} className="h-8 text-xs" placeholder="Gửi ưu đãi sinh nhật" /></div>
          <div><Label className="text-xs">Hiện nút ở</Label>
            <Select value={String(cfg.object ?? 'customer')} onValueChange={(v) => set({ object: v })}>
              <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="customer">Hồ sơ khách hàng</SelectItem><SelectItem value="conversation">Khung hội thoại</SelectItem><SelectItem value="order">Đơn hàng</SelectItem></SelectContent>
            </Select>
            <p className="mt-1 text-[11px] text-muted-foreground">Nhân viên bấm nút → luồng chạy với bản ghi đang mở. Cũng dùng cho form khách gửi.</p>
          </div>
        </>
      )}

      {mode !== 'external' && (
        <div className="space-y-1 rounded-md border p-2">
          <div className="flex items-center justify-between"><Label className="text-xs">Chỉ khi (lọc thêm, VÀ)</Label><Button size="sm" variant="ghost" className="h-6 px-1.5 text-[11px]" onClick={() => set({ filterRules: [...filterRules, { field: truongObj[0]?.path ?? 'customer.name', op: 'eq', value: '' }] })}><Plus className="h-3 w-3" /> Luật</Button></div>
          {filterRules.length === 0 && <p className="text-[11px] text-muted-foreground">Không lọc — mọi bản ghi khớp sự kiện đều chạy.</p>}
          {filterRules.map((r, i) => (
            <div key={i} className="flex gap-1">
              <Input value={r.field} onChange={(e) => suaLoc(i, { field: e.target.value })} list={`truong-${cfg.object}`} className="h-7 font-mono text-[11px]" placeholder="customer.memberRank" />
              <Select value={r.op} onValueChange={(v) => suaLoc(i, { op: v as ConditionRuleV2['op'] })}>
                <SelectTrigger className="h-7 w-28 text-[11px]"><SelectValue /></SelectTrigger>
                <SelectContent>{CONDITION_OPS_V2.map((op) => <SelectItem key={op} value={op}>{CONDITION_OP_V2_LABELS[op]}</SelectItem>)}</SelectContent>
              </Select>
              {!['exists', 'not_exists'].includes(r.op) && <Input value={r.value} onChange={(e) => suaLoc(i, { value: e.target.value })} className="h-7 text-[11px]" placeholder="giá trị" />}
              <Button size="icon" variant="ghost" className="h-7 w-7 shrink-0 text-destructive" onClick={() => set({ filterRules: filterRules.filter((_, k) => k !== i) })}><Trash2 className="h-3 w-3" /></Button>
            </div>
          ))}
          <datalist id={`truong-${cfg.object}`}>{truongObj.map((f) => <option key={f.path} value={f.path}>{f.label}</option>)}{obj && <option value={`${obj.goc}.tag_moi`}>Nhãn vừa gắn/gỡ</option>}</datalist>
        </div>
      )}

      <div className="space-y-1 rounded-md border p-2">
        <Label className="text-xs">Mỗi khách chỉ chạy 1 lần trong (giờ)</Label>
        <Input type="number" min={0} value={String(cfg.motLanMoiKhachGio ?? '')} onChange={(e) => set({ motLanMoiKhachGio: e.target.value === '' ? undefined : Math.max(0, Number(e.target.value)) })} className="h-8 w-28 text-xs" placeholder="0" />
        <p className="text-[11px] text-muted-foreground">Một đơn KiotViet sinh nhiều sự kiện (tạo phiếu → xác nhận → hoàn thành → hoá đơn). Đặt vd 72 để khách không nhận tin lặp; để trống = không giới hạn.</p>
      </div>
    </>
  )
}

function CauHinhAnhXa({ cfg, set }: CfgProps) {
  const { mappings } = useStoreV2()
  const m = mappings.find((x) => x.id === cfg.mappingId)
  return (
    <>
      <div><Label className="text-xs">Bảng ánh xạ</Label>
        <Select value={String(cfg.mappingId ?? '')} onValueChange={(v) => set({ mappingId: v })}>
          <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Chọn bảng" /></SelectTrigger>
          <SelectContent>{mappings.map((x) => <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {m && <div className="max-h-56 overflow-auto rounded-md border text-[11px]"><table className="w-full"><tbody>{m.rules.map((r, i) => <tr key={i} className="border-b last:border-0"><td className="p-1 font-mono">{r.from}</td><td className="p-1 text-muted-foreground">→</td><td className="p-1 font-mono">{r.to}</td></tr>)}</tbody></table></div>}
      <p className="text-[11px] text-muted-foreground">Sửa bảng ở tab Ánh xạ. Sau khối này các khối sau dùng trường CRM (customer.*, order.*).</p>
    </>
  )
}

function ChonTruong({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const fields = useMemo(() => CRM_SCHEMA.flatMap((f) => (f.children ? [f, ...f.children] : [f])) as SchemaField[], [])
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Chọn trường" /></SelectTrigger>
      <SelectContent>{fields.filter((f) => f.type !== 'array' && f.type !== 'object').map((f) => <SelectItem key={f.path} value={f.path}><span className="font-mono">{f.path}</span> <span className="text-muted-foreground">— {f.label}</span></SelectItem>)}</SelectContent>
    </Select>
  )
}

function CauHinhDieuKien({ cfg, set }: CfgProps) {
  const rules = (cfg.rules as ConditionRuleV2[]) ?? []
  const sua = (i: number, p: Partial<ConditionRuleV2>) => set({ rules: rules.map((r, j) => (j === i ? { ...r, ...p } : r)) })
  return (
    <>
      <div className="flex items-center gap-2 text-xs"><span>Kết hợp:</span>
        <Switch checked={cfg.logic === 'or'} onCheckedChange={(v) => set({ logic: v ? 'or' : 'and' })} /><span>{cfg.logic === 'or' ? 'HOẶC (một luật đúng)' : 'VÀ (mọi luật đúng)'}</span>
      </div>
      {rules.map((r, i) => (
        <div key={i} className="space-y-1 rounded-md border p-2">
          <ChonTruong value={r.field} onChange={(v) => sua(i, { field: v })} />
          <div className="flex gap-1">
            <Select value={r.op} onValueChange={(v) => sua(i, { op: v as ConditionRuleV2['op'] })}>
              <SelectTrigger className="h-8 w-40 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>{CONDITION_OPS_V2.map((op) => <SelectItem key={op} value={op}>{CONDITION_OP_V2_LABELS[op]}</SelectItem>)}</SelectContent>
            </Select>
            {!['exists', 'not_exists'].includes(r.op) && <Input value={r.value} onChange={(e) => sua(i, { value: e.target.value })} className="h-8 text-xs" placeholder="Giá trị" />}
            <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0 text-destructive" onClick={() => set({ rules: rules.filter((_, j) => j !== i) })}><Trash2 className="h-3.5 w-3.5" /></Button>
          </div>
          {r.field.includes('[]') && <p className="text-[10.5px] text-muted-foreground">Trường trong mảng: ĐÚNG khi <b>bất kỳ</b> dòng hàng nào thoả.</p>}
        </div>
      ))}
      <Button size="sm" variant="outline" onClick={() => set({ rules: [...rules, { field: 'order.status', op: 'eq', value: '' }] })}><Plus className="h-3.5 w-3.5" /> Thêm luật</Button>
    </>
  )
}

function CauHinhCho({ cfg, set }: CfgProps) {
  return (
    <div className="space-y-1">
    <div className="flex gap-2">
      <div className="flex-1"><Label className="text-xs">Thời gian</Label><Input type="number" min={1} value={String(cfg.value ?? 1)} onChange={(e) => set({ value: Number(e.target.value) })} className="h-8" /></div>
      <div className="flex-1"><Label className="text-xs">Đơn vị</Label>
        <Select value={String(cfg.unit ?? 'hours')} onValueChange={(v) => set({ unit: v })}>
          <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="minutes">Phút</SelectItem><SelectItem value="hours">Giờ</SelectItem><SelectItem value="days">Ngày</SelectItem>
            <SelectItem value="until_friend">Đến khi khách đồng ý kết bạn (tối đa … giờ)</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
    {cfg.unit === 'until_friend' && <p className="text-[11px] text-muted-foreground">Luồng dừng ở đây tới khi khách bấm đồng ý kết bạn; quá thời gian trên thì đi tiếp để nhánh sau xử lý (gọi điện, ZNS…). Dùng điều kiện <code>customer.isFriend</code> ngay sau khối này.</p>}
    </div>
  )
}

/** Ô văn bản có nút chèn biến {{…}} từ schema CRM + biến do hành động sinh. */
function OVanBan({ nhan, value, onChange, rows = 4, goiY }: { nhan: string; value: string; onChange: (v: string) => void; rows?: number; goiY?: string }) {
  const [mo, setMo] = useState(false)
  const fields = useMemo(() => [
    ...CRM_SCHEMA.flatMap((f) => (f.children ? f.children : [f])).filter((f) => f.type !== 'array'),
    { path: 'order.items[0].name', label: 'Tên SP đầu tiên', type: 'string' as const },
    { path: 'voucher.code', label: 'Mã voucher (sau khối Tạo voucher)', type: 'string' as const },
    { path: 'voucher.giaTri', label: 'Giá trị voucher', type: 'number' as const },
  ], [])
  return (
    <div>
      <div className="flex items-center justify-between"><Label className="text-xs">{nhan}</Label><button type="button" className="inline-flex items-center gap-1 text-[11px] text-primary hover:underline" onClick={() => setMo((v) => !v)}><Braces className="h-3 w-3" /> Chèn biến</button></div>
      {mo && <div className="mb-1 flex max-h-32 flex-wrap gap-1 overflow-y-auto rounded-md border p-1.5">{fields.map((f) => <button key={f.path} type="button" title={f.label} className="rounded bg-muted px-1.5 py-0.5 font-mono text-[10.5px] hover:bg-accent" onClick={() => onChange(`${value}{{${f.path}}}`)}>{f.path}</button>)}</div>}
      <Textarea rows={rows} value={value} onChange={(e) => onChange(e.target.value)} className="text-xs" />
      {goiY && <p className="mt-0.5 text-[10.5px] text-muted-foreground">{goiY}</p>}
    </div>
  )
}

function CauHinhHanhDong({ cfg, set }: CfgProps) {
  const type = String(cfg.type ?? 'add_tag')
  const S = (k: string) => String(cfg[k] ?? '')
  return (
    <>
      <div><Label className="text-xs">Loại hành động</Label>
        <Select value={type} onValueChange={(v) => set({ type: v })}>
          <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>{ACTIONS_V2.map((a) => <SelectItem key={a} value={a}>{ACTION_V2_LABELS[a]}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {(type === 'add_tag' || type === 'remove_tag') && <div><Label className="text-xs">Nhãn</Label><Input value={S('tag')} onChange={(e) => set({ tag: e.target.value })} className="h-8" placeholder="Đang dùng kháng sinh" /></div>}
      {type === 'send_zalo_personal' && <p className="rounded-md border bg-muted/40 p-2 text-[11px] text-muted-foreground">Gửi bằng nick Zalo cá nhân đang kết nối. Chỉ cần SĐT khách — chưa có hội thoại thì hệ thống tự tra Zalo theo SĐT và mở hội thoại. Nên đặt sau khối "Chờ kết bạn" để tin vào mục bạn bè của khách.</p>}
      {type === 'send_zalo_oa' && (() => {
        const kieu = S('kieuTin') || (S('message') ? 'tu_soan' : 'mau')
        return (
          <div className="space-y-2">
            <div><Label className="text-xs">Kiểu tin OA</Label>
              <Select value={kieu} onValueChange={(v) => set({ kieuTin: v })}>
                <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="mau">Theo mẫu OA (ZNS) — gửi theo SĐT, nội dung lấy từ mẫu</SelectItem>
                  <SelectItem value="tu_soan">Tin tư vấn tự soạn — chỉ khách đã nhắn/quan tâm OA</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {kieu === 'mau' ? (
              <div className="space-y-1 rounded-md border p-2">
                <p className="text-[11px] text-muted-foreground">Nội dung tin là nội dung mẫu đã được Zalo duyệt; chỉ cần ghép dữ liệu vào tham số. Gửi theo SĐT khách, tính phí ví ZNS.</p>
                <ChonMauZns templateId={S('znsTemplateId')} params={S('znsParams')} onChange={(v) => set({ znsTemplateId: v.templateId, znsParams: v.params })} />
              </div>
            ) : (
              <>
                <OVanBan nhan="Nội dung tin tư vấn" value={S('message')} onChange={(v) => set({ message: v })} goiY="OA chỉ nhắn tự do được khách đã tương tác gần đây (hồ sơ có SĐT để ghép với đơn)." />
                <div className="space-y-1 rounded-md border p-2">
                  <Label className="text-xs">Mẫu dự phòng (tuỳ chọn)</Label>
                  <p className="text-[11px] text-muted-foreground">Khách chưa tương tác hoặc quá hạn tư vấn → gửi tin theo mẫu này.</p>
                  <ChonMauZns templateId={S('znsTemplateId')} params={S('znsParams')} onChange={(v) => set({ znsTemplateId: v.templateId, znsParams: v.params })} />
                  {!!S('znsTemplateId') && <Button type="button" size="sm" variant="ghost" className="h-6 px-1.5 text-[11px] text-muted-foreground" onClick={() => set({ znsTemplateId: '', znsParams: '' })}>Bỏ mẫu dự phòng</Button>}
                </div>
              </>
            )}
          </div>
        )
      })()}
      {(type === 'send_message' || type === 'send_zalo_personal' || type === 'send_survey') && (
        <>
          {(type === 'send_message' || type === 'send_survey') && <div><Label className="text-xs">Kênh gửi</Label>
            <Select value={S('channel') || 'auto'} onValueChange={(v) => set({ channel: v === 'auto' ? '' : v })}>
              <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="zalo_oa">Zalo OA (ghép theo SĐT)</SelectItem><SelectItem value="zalo_personal">Zalo cá nhân (nick phụ trách)</SelectItem><SelectItem value="facebook">Facebook (nếu có hội thoại)</SelectItem><SelectItem value="auto">Tự chọn — hội thoại gần nhất</SelectItem></SelectContent>
            </Select>
            <p className="mt-1 text-[11px] text-muted-foreground">Chọn kênh nào chỉ gửi qua kênh đó — Zalo cá nhân và OA nên dùng hai hành động riêng.</p>
          </div>}
          {S('channel') === 'zalo_oa' && (
            <div className="space-y-1 rounded-md border p-2">
              <Label className="text-xs">ZNS dự phòng (tuỳ chọn)</Label>
              <p className="text-[11px] text-muted-foreground">OA chỉ nhắn tư vấn được khách đã nhắn/quan tâm OA gần đây. Khách chưa tương tác hoặc quá hạn → gửi ZNS theo SĐT bằng mẫu đã duyệt (tính phí ví ZNS).</p>
              <ChonMauZns templateId={S('znsTemplateId')} params={S('znsParams')} onChange={(v) => set({ znsTemplateId: v.templateId, znsParams: v.params })} />
              {!!S('znsTemplateId') && <Button type="button" size="sm" variant="ghost" className="h-6 px-1.5 text-[11px] text-muted-foreground" onClick={() => set({ znsTemplateId: '', znsParams: '' })}>Bỏ ZNS dự phòng</Button>}
            </div>
          )}
          <OVanBan nhan="Nội dung tin" value={S('message')} onChange={(v) => set({ message: v })} goiY="Biến thay bằng dữ liệu đã ánh xạ. Không có SĐT khách thì hành động báo lỗi — nên rẽ nhánh sang Tạo việc." />
          {type === 'send_survey' && <div><Label className="text-xs">Link form khảo sát</Label><Input value={S('formUrl')} onChange={(e) => set({ formUrl: e.target.value })} className="h-8" placeholder="https://forms.gle/…" /></div>}
        </>
      )}
      {type === 'send_zalo_zns' && (<>
        <ChonMauZns templateId={S('templateId')} params={S('params')} onChange={(v) => set({ templateId: v.templateId, params: v.params })} />
        <p className="text-[11px] text-muted-foreground">ZNS gửi theo SĐT khách, không cần khách quan tâm OA; tính phí vào ví ZNS của OA.</p>
      </>)}
      {type === 'create_voucher' && (<>
        <div className="grid grid-cols-2 gap-2">
          <div><Label className="text-xs">Tiền tố mã</Label><Input value={S('prefix') || 'MVS'} onChange={(e) => set({ prefix: e.target.value })} className="h-8" /></div>
          <div><Label className="text-xs">Giá trị</Label><Input type="number" value={S('value')} onChange={(e) => set({ value: Number(e.target.value) })} className="h-8" /></div>
          <div><Label className="text-xs">Loại</Label>
            <Select value={S('kind') || 'percent'} onValueChange={(v) => set({ kind: v })}><SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="percent">% giảm</SelectItem><SelectItem value="amount">Số tiền</SelectItem></SelectContent></Select>
          </div>
          <div><Label className="text-xs">Hạn (ngày)</Label><Input type="number" value={S('expireDays') || '30'} onChange={(e) => set({ expireDays: Number(e.target.value) })} className="h-8" /></div>
        </div>
        <OVanBan nhan="Ghi chú" value={S('note')} onChange={(v) => set({ note: v })} rows={2} goiY="Sau khối này dùng {{voucher.code}} trong tin nhắn." />
      </>)}
      {type === 'create_task' && (<>
        <OVanBan nhan="Tiêu đề việc" value={S('title')} onChange={(v) => set({ title: v })} rows={2} />
        <div className="grid grid-cols-2 gap-2">
          <div><Label className="text-xs">Giao cho</Label><Input value={S('assignee')} onChange={(e) => set({ assignee: e.target.value })} className="h-8" placeholder="Nhóm CSKH" /></div>
          <div><Label className="text-xs">Hạn (giờ)</Label><Input type="number" value={S('dueHours') || '24'} onChange={(e) => set({ dueHours: Number(e.target.value) })} className="h-8" /></div>
        </div>
      </>)}
      {type === 'update_property' && (<>
        <div><Label className="text-xs">Tên thuộc tính CDP</Label><Input value={S('key')} onChange={(e) => set({ key: e.target.value })} className="h-8" placeholder="don_gan_nhat" /></div>
        <OVanBan nhan="Giá trị" value={S('value')} onChange={(v) => set({ value: v })} rows={2} />
      </>)}
      {type === 'write_sheet' && (<>
        <div><Label className="text-xs">Tên tab Sheet</Label><Input value={S('sheet')} onChange={(e) => set({ sheet: e.target.value })} className="h-8" placeholder="Nhật ký chăm sóc" /></div>
        <OVanBan nhan="Cột ghi (phẩy, mỗi cột là một trường)" value={S('columns')} onChange={(v) => set({ columns: v })} rows={2} goiY="vd: customer.name, customer.phone, order.code, order.total" />
      </>)}
      {type === 'http_request' && (<>
        <div className="grid grid-cols-[6rem_1fr] gap-2">
          <div><Label className="text-xs">Method</Label><Select value={S('method') || 'POST'} onValueChange={(v) => set({ method: v })}><SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger><SelectContent>{['GET', 'POST', 'PUT', 'PATCH'].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent></Select></div>
          <div><Label className="text-xs">URL</Label><Input value={S('url')} onChange={(e) => set({ url: e.target.value })} className="h-8" placeholder="https://api.doi-tac.vn/…" /></div>
        </div>
        <OVanBan nhan="Body (JSON, có biến)" value={S('body')} onChange={(v) => set({ body: v })} rows={4} />
      </>)}
      {type === 'send_notification' && <OVanBan nhan="Nội dung thông báo nội bộ" value={S('message')} onChange={(v) => set({ message: v })} rows={3} />}
      {type === 'send_friend_request' && (<>
        <div><Label className="text-xs">Trường chứa số điện thoại</Label>
          <Select value={S('phoneField') || 'customer.phone'} onValueChange={(v) => set({ phoneField: v })}>
            <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="customer.phone">customer.phone (SĐT trên đơn)</SelectItem></SelectContent>
          </Select>
        </div>
        <OVanBan nhan="Lời mời kết bạn (ngắn, ≤ 100 ký tự)" value={S('message')} onChange={(v) => set({ message: v })} rows={3}
          goiY="Zalo giới hạn 20 lời mời/ngày mỗi nick. Khách chưa đồng ý thì các bước sau nên rẽ nhánh sang gọi điện." />
      </>)}
    </>
  )
}
